import React, { ReactElement, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import enumStrings, { RoutePaths } from "../../../enumStrings";
import ResetPassword from "./ResetPassword";
import { resetPassword } from "aws-amplify/auth";
import BaseAuthPage from "./BaseAuthPage";
import LinkText from "../../common/LinkText";
import TextField from "../../common/TextField";
import Button from "../../common/Button";
import { commonStyles } from "../../common/commonStyles";

import { getAuthErrorName, temporaryPasswordGuidance } from "./authErrors";
import AuthErrorMessage from "./AuthErrorMessage";

interface ForgotPasswordInput {
  email: string;
}

const ForgotPassword = (): ReactElement => {
  const initialValues: ForgotPasswordInput = {
    email: "",
  };
  const validationSchema = yup.object({
    email: yup.string().required(enumStrings.pages.common.validation.email),
  });
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState(1); // 1 = email stage, 2 = code stage

  const [customError, setCustomError] = useState("");
  const [loader, setLoader] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: ForgotPasswordInput) => {
      setCustomError("");
      setLoader(true);
      try {
        setEmail(input.email.trim().toLowerCase());
        await resetPassword({
          username: input.email.trim().toLowerCase(),
        });
        setStage(2);
      } catch (error: unknown) {
        switch (getAuthErrorName(error)) {
          case "UserNotFoundException":
            setCustomError(enumStrings.pages.common.errors.emailDoesNotExist);
            break;
          case "NotAuthorizedException":
          case "InvalidParameterException":
            setCustomError(temporaryPasswordGuidance);
            break;
          case "LimitExceededException":
          case "TooManyRequestsException":
            setCustomError(enumStrings.pages.common.errors.tooManyAttempts);
            break;
          case "NetworkError":
            setCustomError(enumStrings.pages.common.errors.networkError);
            break;
          default:
            setCustomError("We could not send a reset code. Try again. If it keeps happening, contact your chamber administrator.");
        }
      } finally {
        setLoader(false);
      }
    },
  });
  return (
    <BaseAuthPage logoSubtitle={enumStrings.pages.forgotPassword.logoSubtitle}>
      {stage === 1 && (
        <form
          style={{
            width: "100%",
          }}
          onSubmit={formik.handleSubmit}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: commonStyles.fontSizes.medium,
                pb: 2,
              }}
            >
              {enumStrings.pages.forgotPassword.description}
            </Typography>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <AuthErrorMessage message={customError} />
            <Button
              text={enumStrings.pages.forgotPassword.buttonText}
              disabled={loader}
              isLoading={loader}
              fullWidth
              size="large"
              type="submit"
              additionalSx={{
                mt: 4,
              }}
            />
            <LinkText
              to={RoutePaths.login}
              text={enumStrings.pages.common.buttons.goBackToLoginText}
            />
          </Box>
        </form>
      )}
      {stage === 2 && <ResetPassword email={email} />}
    </BaseAuthPage>
  );
};
export default ForgotPassword;
